import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { useToast } from '../../../../hooks/use-toast';
import {
  listBlacklistedDomains,
  createBlacklistedDomain,
  deleteBlacklistedDomain,
} from '../../../../services/AdminApis';

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DirectoryDomainManager() {
  const { toast } = useToast();
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState('');
  const [applyToSerp, setApplyToSerp] = useState('local'); // 'local' | 'serp'
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const canSubmit = useMemo(
    () => domain.trim().includes('.') && !submitting,
    [domain, submitting]
  );

  const loadDomains = async () => {
    setLoading(true);
    try {
      const data = await listBlacklistedDomains();
      setDomains(data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to load blacklisted domains.';
      toast({ variant: 'destructive', title: 'Unable to load domains', description: detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDomains();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await createBlacklistedDomain({
        domain: domain.trim(),
        apply_to_serp: applyToSerp === 'serp',
      });
      toast({ title: 'Domain added', description: `${domain.trim()} will be excluded from scraping.` });
      setDomain('');
      setApplyToSerp('local');
      await loadDomains();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to add domain.';
      toast({ variant: 'destructive', title: 'Domain not added', description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    setDeletingId(row.id);
    try {
      await deleteBlacklistedDomain(row.id);
      toast({ title: 'Domain removed', description: `${row.domain} is no longer excluded.` });
      await loadDomains();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to remove domain.';
      toast({ variant: 'destructive', title: 'Unable to remove', description: detail });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Directory Domains</h1>
          <p className="text-sm text-gray-500 mt-1">
            Aggregator/directory domains excluded from prospect scraping (e.g. justdial.com, sulekha.com).
          </p>
        </div>
        <Button type="button" variant="outline" onClick={loadDomains} disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Add Directory Domain</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-[2fr_1.5fr_auto] md:items-end">
            <div>
              <Label>Domain</Label>
              <Input
                type="text"
                placeholder="justdial.com"
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">Paste a URL or bare domain — it will be normalized.</p>
            </div>

            <div>
              <Label>Exclusion Mode</Label>
              <Select value={applyToSerp} onValueChange={setApplyToSerp}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Filter locally only</SelectItem>
                  <SelectItem value="serp">Exclude at search (-site:)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                "-site:" keeps it out of organic results entirely (a few are capped per query).
              </p>
            </div>

            <Button type="submit" disabled={!canSubmit} className="bg-purple-700 hover:bg-purple-800 text-white">
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
              Add
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Blacklisted Domains</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>ID</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-gray-500 py-8">
                      {loading ? 'Loading domains...' : 'No blacklisted domains yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {domains.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.id}</TableCell>
                    <TableCell>{row.domain}</TableCell>
                    <TableCell>
                      <Badge className={row.apply_to_serp ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'}>
                        {row.apply_to_serp ? '-site: + local' : 'local only'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(row.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(row)}
                        disabled={deletingId === row.id}
                        className="text-red-600 hover:text-red-700"
                      >
                        {deletingId === row.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
